/**
 * RoutedDriver — composes multiple concrete drivers and delegates per-collection.
 *
 * The `collectionRouting` map (in featureFlags.js) specifies which backend
 * each collection should use. This driver reads the map on every call and
 * routes to the appropriate concrete driver. Historical context: Sessions 3-6
 * of the Convex migration used this to progressively flip collections from
 * 'firebase' to 'convex' per-collection; Session 10 (2026-04-20) completed the
 * migration by deleting the Firebase driver entirely. The routing map still
 * exists as infrastructure for future multi-backend scenarios (e.g., Campaign
 * feature might route per-tenant), but at rest today all routes go to Convex.
 *
 * Composite operations (`updateMultiple`, `pushAuditEvent`, `pushAuditSession`)
 * dispatch smartly:
 *  - `updateMultiple` buckets patches by the routed destination of each path.
 *  - `pushAuditEvent` / `pushAuditSession` route to wherever `audit` is routed
 *    (NOT `compositeDefault`) so events and the data they mutate live in the
 *    same backend. Before this was made path-aware, the fallback meant
 *    session-5-routed audit got READ from Convex while appends were written
 *    to Firebase — orphaning all events.
 *
 * This driver lives behind the `FEATURE_FLAGS.useRoutedDriver` flag. When the
 * flag is off, useStorage skips composition and uses a single backend driver
 * directly — identical to pre-extraction behavior.
 */

import { COLLECTION_PATHS } from './StorageDriver.js';

/**
 * @param {Object} config
 * @param {Record<string, 'convex'|'local'>} config.routing
 *   Per-path driver assignment. See featureFlags.js.
 * @param {Object} config.drivers
 *   Map from kind to concrete driver instance. Only required entries are
 *   drivers actually referenced in `routing`; missing drivers fall back to
 *   'local'.
 * @param {'convex'|'local'} [config.compositeDefault]
 *   Which driver handles updateMultiple / pushAuditEvent / pushAuditSession.
 *   Defaults to 'convex' (Session 10 — Firebase deleted).
 * @returns {import('./StorageDriver.js').StorageDriver}
 */
export const createRoutedDriver = ({ routing, drivers, compositeDefault = 'convex' }) => {
  let disposed = false;

  const pickDriverForPath = (path) => {
    const kind = routing[path] || 'convex';
    return drivers[kind] || drivers.convex || drivers.local;
  };

  const compositeDriver = drivers[compositeDefault] || drivers.convex || drivers.local;

  // Per-handler bookkeeping: we might need to re-subscribe if the routing for
  // a path changes at runtime. For Session 2 the routing is static at mount
  // time, so we keep it simple: subscribe once per path via the selected
  // driver, return the concrete driver's unsubscribe.

  return {
    kind: 'routed',

    setViewerAuthToken(nextToken) {
      Object.values(drivers).forEach(d => {
        if (typeof d?.setViewerAuthToken !== 'function') return;
        try { d.setViewerAuthToken(nextToken); } catch (e) { console.error(e); }
      });
    },

    setSubscriptionArgs(path, args) {
      const d = pickDriverForPath(path);
      if (!d || typeof d.setSubscriptionArgs !== 'function') return;
      return d.setSubscriptionArgs(path, args);
    },

    subscribe(path, handler) {
      const d = pickDriverForPath(path);
      if (!d) {
        console.warn(`RoutedDriver: no driver available for path ${path}`);
        return () => {};
      }
      return d.subscribe(path, handler);
    },

    save(path, value, options) {
      const d = pickDriverForPath(path);
      if (!d) {
        console.warn(`RoutedDriver: no driver available for path ${path}`);
        return;
      }
      return d.save(path, value, options);
    },

    async upsertJournalEntry(args) {
      const d = pickDriverForPath('journal');
      if (!d || typeof d.upsertJournalEntry !== 'function') return null;
      return await d.upsertJournalEntry(args);
    },

    async deleteJournalEntry(args) {
      const d = pickDriverForPath('journal');
      if (!d || typeof d.deleteJournalEntry !== 'function') return null;
      return await d.deleteJournalEntry(args);
    },

    async setJournalEntryCampaignScope(args) {
      const d = pickDriverForPath('journal');
      if (!d || typeof d.setJournalEntryCampaignScope !== 'function') return null;
      return await d.setJournalEntryCampaignScope(args);
    },

    async upsertDmJournalEntry(args) {
      const d = pickDriverForPath('dmJournal');
      if (!d || typeof d.upsertDmJournalEntry !== 'function') return null;
      return await d.upsertDmJournalEntry(args);
    },

    async deleteDmJournalEntry(args) {
      const d = pickDriverForPath('dmJournal');
      if (!d || typeof d.deleteDmJournalEntry !== 'function') return null;
      return await d.deleteDmJournalEntry(args);
    },

    async upsertGlobalLoreEntry(args) {
      const d = pickDriverForPath('journal');
      if (!d || typeof d.upsertGlobalLoreEntry !== 'function') return null;
      return await d.upsertGlobalLoreEntry(args);
    },

    async deleteGlobalLoreEntry(args) {
      const d = pickDriverForPath('journal');
      if (!d || typeof d.deleteGlobalLoreEntry !== 'function') return null;
      return await d.deleteGlobalLoreEntry(args);
    },

    async lookupJournalEntriesByName(args) {
      const d = pickDriverForPath('journal');
      if (!d || typeof d.lookupJournalEntriesByName !== 'function') return null;
      return await d.lookupJournalEntriesByName(args);
    },

    async lookupDmJournalEntriesByName(args) {
      const d = pickDriverForPath('dmJournal');
      if (!d || typeof d.lookupDmJournalEntriesByName !== 'function') return null;
      return await d.lookupDmJournalEntriesByName(args);
    },

    async discoverJournalEntries(args) {
      const d = pickDriverForPath('journal');
      if (!d || typeof d.discoverJournalEntries !== 'function') return null;
      return await d.discoverJournalEntries(args);
    },

    async discoverDmJournalEntries(args) {
      const d = pickDriverForPath('dmJournal');
      if (!d || typeof d.discoverDmJournalEntries !== 'function') return null;
      return await d.discoverDmJournalEntries(args);
    },

    async updateMultiple(patch) {
      // Split the patch by top-level path and dispatch each slice to its
      // assigned driver. Within a single driver's updateMultiple, writes are
      // atomic; across drivers, atomicity is best-effort (parallel fire-and-
      // forget). This matches the "you pay what you use" tradeoff of having
      // data split across backends — true cross-backend transactions require
      // a coordinator we don't have.
      if (!patch || typeof patch !== 'object') return;
      const bucketsByKind = new Map();
      for (const [key, value] of Object.entries(patch)) {
        const top = key.split('/')[0];
        const kind = routing[top] || compositeDefault;
        let bucket = bucketsByKind.get(kind);
        if (!bucket) { bucket = {}; bucketsByKind.set(kind, bucket); }
        bucket[key] = value;
      }
      const promises = [];
      for (const [kind, bucket] of bucketsByKind) {
        const d = drivers[kind];
        if (!d) continue;
        promises.push(d.updateMultiple(bucket));
      }
      await Promise.all(promises);
    },

    pushAuditEvent(event) {
      // Route to whichever backend holds `audit` in the current routing map.
      // Reading from Convex while writing to Firebase (the old bug) left
      // session IDs in one backend and activeSessionId pointers in the other.
      const d = pickDriverForPath('audit');
      return (d || compositeDriver).pushAuditEvent(event);
    },

    pushAuditSession(session) {
      const d = pickDriverForPath('audit');
      return (d || compositeDriver).pushAuditSession(session);
    },

    async markStatusMessageSeen(args) {
      const d = pickDriverForPath('messages');
      if (!d || typeof d.markStatusMessageSeen !== 'function') return null;
      return await d.markStatusMessageSeen(args);
    },

    async dismissStatusMessage(args) {
      const d = pickDriverForPath('messages');
      if (!d || typeof d.dismissStatusMessage !== 'function') return null;
      return await d.dismissStatusMessage(args);
    },

    async markDmChatRead(args) {
      const d = pickDriverForPath('messages');
      if (!d || typeof d.markDmChatRead !== 'function') return null;
      return await d.markDmChatRead(args);
    },

    async sendStatusMessage(args) {
      const d = pickDriverForPath('messages');
      if (!d || typeof d.sendStatusMessage !== 'function') return null;
      return await d.sendStatusMessage(args);
    },

    async sendDmChatMessage(args) {
      const d = pickDriverForPath('messages');
      if (!d || typeof d.sendDmChatMessage !== 'function') return null;
      return await d.sendDmChatMessage(args);
    },

    async deleteDmChatMessage(args) {
      const d = pickDriverForPath('messages');
      if (!d || typeof d.deleteDmChatMessage !== 'function') return null;
      return await d.deleteDmChatMessage(args);
    },

    async patchCharacterVitals(args) {
      const charDriver = pickDriverForPath('chars');
      if (!charDriver) return null;
      if (typeof charDriver.patchCharacterVitals !== 'function') return null;
      return await charDriver.patchCharacterVitals(args);
    },

    async patchCharacterSystemInstance(args) {
      const charDriver = pickDriverForPath('chars');
      if (!charDriver) return null;
      if (typeof charDriver.patchCharacterSystemInstance !== 'function') return null;
      return await charDriver.patchCharacterSystemInstance(args);
    },

    async patchCharacterSystemState(args) {
      const charDriver = pickDriverForPath('chars');
      if (!charDriver) return null;
      if (typeof charDriver.patchCharacterSystemState !== 'function') return null;
      return await charDriver.patchCharacterSystemState(args);
    },

    async patchCharacterSystemStateAndVitals(args) {
      const charDriver = pickDriverForPath('chars');
      if (!charDriver) return null;
      if (typeof charDriver.patchCharacterSystemStateAndVitals !== 'function') return null;
      return await charDriver.patchCharacterSystemStateAndVitals(args);
    },

    async patchCharacterSystemStateAndResources(args) {
      const charDriver = pickDriverForPath('chars');
      if (!charDriver) return null;
      if (typeof charDriver.patchCharacterSystemStateAndResources !== 'function') return null;
      return await charDriver.patchCharacterSystemStateAndResources(args);
    },

    async patchCharacterSystemStateAndEffects(args) {
      const charDriver = pickDriverForPath('chars');
      if (!charDriver) return null;
      if (typeof charDriver.patchCharacterSystemStateAndEffects !== 'function') return null;
      return await charDriver.patchCharacterSystemStateAndEffects(args);
    },

    async depositPartyInventoryFromChar(args) {
      const charDriver = pickDriverForPath('chars');
      const partyDriver = pickDriverForPath('partyInventory');
      if (!charDriver || !partyDriver || charDriver !== partyDriver) return null;
      if (typeof partyDriver.depositPartyInventoryFromChar !== 'function') return null;
      return await partyDriver.depositPartyInventoryFromChar(args);
    },

    async takePartyInventoryToChar(args) {
      const charDriver = pickDriverForPath('chars');
      const partyDriver = pickDriverForPath('partyInventory');
      if (!charDriver || !partyDriver || charDriver !== partyDriver) return null;
      if (typeof partyDriver.takePartyInventoryToChar !== 'function') return null;
      return await partyDriver.takePartyInventoryToChar(args);
    },

    async transferInventoryItemBetweenChars(args) {
      const charDriver = pickDriverForPath('chars');
      if (!charDriver) return null;
      if (typeof charDriver.transferInventoryItemBetweenChars !== 'function') return null;
      return await charDriver.transferInventoryItemBetweenChars(args);
    },

    async moveCharItemToCraftingTable(args) {
      const charDriver = pickDriverForPath('chars');
      if (!charDriver) return null;
      if (typeof charDriver.moveCharItemToCraftingTable !== 'function') return null;
      return await charDriver.moveCharItemToCraftingTable(args);
    },

    async addInventoryItem(args) {
      const charDriver = pickDriverForPath('chars');
      if (!charDriver) return null;
      if (typeof charDriver.addInventoryItem !== 'function') return null;
      return await charDriver.addInventoryItem(args);
    },

    async updateInventoryItem(args) {
      const charDriver = pickDriverForPath('chars');
      if (!charDriver) return null;
      if (typeof charDriver.updateInventoryItem !== 'function') return null;
      return await charDriver.updateInventoryItem(args);
    },

    async removeInventoryItem(args) {
      const charDriver = pickDriverForPath('chars');
      if (!charDriver) return null;
      if (typeof charDriver.removeInventoryItem !== 'function') return null;
      return await charDriver.removeInventoryItem(args);
    },

    async setInventoryItemFavorite(args) {
      const charDriver = pickDriverForPath('chars');
      if (!charDriver) return null;
      if (typeof charDriver.setInventoryItemFavorite !== 'function') return null;
      return await charDriver.setInventoryItemFavorite(args);
    },

    async toggleInventoryItemEquipped(args) {
      const charDriver = pickDriverForPath('chars');
      if (!charDriver) return null;
      if (typeof charDriver.toggleInventoryItemEquipped !== 'function') return null;
      return await charDriver.toggleInventoryItemEquipped(args);
    },

    async consumeInventoryItem(args) {
      const charDriver = pickDriverForPath('chars');
      if (!charDriver) return null;
      if (typeof charDriver.consumeInventoryItem !== 'function') return null;
      return await charDriver.consumeInventoryItem(args);
    },

    async activateInventoryItemAbility(args) {
      const charDriver = pickDriverForPath('chars');
      if (!charDriver) return null;
      if (typeof charDriver.activateInventoryItemAbility !== 'function') return null;
      return await charDriver.activateInventoryItemAbility(args);
    },

    async craftOutputToChar(args) {
      const charDriver = pickDriverForPath('chars');
      if (!charDriver) return null;
      if (typeof charDriver.craftOutputToChar !== 'function') return null;
      return await charDriver.craftOutputToChar(args);
    },

    async craftOutputToParty(args) {
      const charDriver = pickDriverForPath('chars');
      const partyDriver = pickDriverForPath('partyInventory');
      if (!charDriver || !partyDriver || charDriver !== partyDriver) return null;
      if (typeof partyDriver.craftOutputToParty !== 'function') return null;
      return await partyDriver.craftOutputToParty(args);
    },

    async tableRowToChar(args) {
      const charDriver = pickDriverForPath('chars');
      if (!charDriver) return null;
      if (typeof charDriver.tableRowToChar !== 'function') return null;
      return await charDriver.tableRowToChar(args);
    },

    async tableRowToParty(args) {
      const charDriver = pickDriverForPath('chars');
      const partyDriver = pickDriverForPath('partyInventory');
      if (!charDriver || !partyDriver || charDriver !== partyDriver) return null;
      if (typeof partyDriver.tableRowToParty !== 'function') return null;
      return await partyDriver.tableRowToParty(args);
    },

    async activateCombatItemAbility(args) {
      const charDriver = pickDriverForPath('chars');
      const combatDriver = pickDriverForPath('combat');
      if (!charDriver || !combatDriver || charDriver !== combatDriver) return null;
      if (typeof charDriver.activateCombatItemAbility !== 'function') return null;
      return await charDriver.activateCombatItemAbility(args);
    },

    async applyEffectInstanceLifecycle(args) {
      const charDriver = pickDriverForPath('chars');
      if (!charDriver) return null;
      if (typeof charDriver.applyEffectInstanceLifecycle !== 'function') return null;
      return await charDriver.applyEffectInstanceLifecycle(args);
    },

    async reconcileItemAbilityEffectInstancesForChar(args) {
      const charDriver = pickDriverForPath('chars');
      if (!charDriver) return null;
      if (typeof charDriver.reconcileItemAbilityEffectInstancesForChar !== 'function') return null;
      return await charDriver.reconcileItemAbilityEffectInstancesForChar(args);
    },

    async purchaseShopListing(args) {
      const charDriver = pickDriverForPath('chars');
      const shopDriver = pickDriverForPath('shopRuntime');
      if (!charDriver || !shopDriver || charDriver !== shopDriver) return null;
      if (typeof charDriver.purchaseShopListing !== 'function') return null;
      return await charDriver.purchaseShopListing(args);
    },

    async checkoutShopCart(args) {
      const charDriver = pickDriverForPath('chars');
      const shopDriver = pickDriverForPath('shopRuntime');
      if (!charDriver || !shopDriver || charDriver !== shopDriver) return null;
      if (typeof charDriver.checkoutShopCart !== 'function') return null;
      return await charDriver.checkoutShopCart(args);
    },

    async sellInventoryItemToShopForDm(args) {
      const charDriver = pickDriverForPath('chars');
      const shopDriver = pickDriverForPath('shopRuntime');
      if (!charDriver || !shopDriver || charDriver !== shopDriver) return null;
      if (typeof charDriver.sellInventoryItemToShopForDm !== 'function') return null;
      return await charDriver.sellInventoryItemToShopForDm(args);
    },

    async submitTradeOffer(args) {
      const charDriver = pickDriverForPath('chars');
      const shopDriver = pickDriverForPath('shopRuntime');
      if (!charDriver || !shopDriver || charDriver !== shopDriver) return null;
      if (typeof charDriver.submitTradeOffer !== 'function') return null;
      return await charDriver.submitTradeOffer(args);
    },

    async cancelTradeOffer(args) {
      const charDriver = pickDriverForPath('chars');
      const shopDriver = pickDriverForPath('shopRuntime');
      if (!charDriver || !shopDriver || charDriver !== shopDriver) return null;
      if (typeof charDriver.cancelTradeOffer !== 'function') return null;
      return await charDriver.cancelTradeOffer(args);
    },

    async denyTradeOfferForDm(args) {
      const charDriver = pickDriverForPath('chars');
      const shopDriver = pickDriverForPath('shopRuntime');
      if (!charDriver || !shopDriver || charDriver !== shopDriver) return null;
      if (typeof charDriver.denyTradeOfferForDm !== 'function') return null;
      return await charDriver.denyTradeOfferForDm(args);
    },

    async settleTradeOfferForDm(args) {
      const charDriver = pickDriverForPath('chars');
      const shopDriver = pickDriverForPath('shopRuntime');
      if (!charDriver || !shopDriver || charDriver !== shopDriver) return null;
      if (typeof charDriver.settleTradeOfferForDm !== 'function') return null;
      return await charDriver.settleTradeOfferForDm(args);
    },

    async promoteTradeReceiptLineToShopListingForDm(args) {
      const charDriver = pickDriverForPath('chars');
      const shopDriver = pickDriverForPath('shopRuntime');
      if (!charDriver || !shopDriver || charDriver !== shopDriver) return null;
      if (typeof charDriver.promoteTradeReceiptLineToShopListingForDm !== 'function') return null;
      return await charDriver.promoteTradeReceiptLineToShopListingForDm(args);
    },

    async adjustShopWalletForDm(args) {
      const shopDriver = pickDriverForPath('shopRuntime');
      if (!shopDriver) return null;
      if (typeof shopDriver.adjustShopWalletForDm !== 'function') return null;
      return await shopDriver.adjustShopWalletForDm(args);
    },

    async importShopDataForDm(args) {
      const shopDriver = pickDriverForPath('shopRuntime');
      if (!shopDriver) return null;
      if (typeof shopDriver.importShopDataForDm !== 'function') return null;
      return await shopDriver.importShopDataForDm(args);
    },

    async createDmShop(args) {
      const shopDriver = pickDriverForPath('shopRuntime');
      if (!shopDriver) return null;
      if (typeof shopDriver.createDmShop !== 'function') return null;
      return await shopDriver.createDmShop(args);
    },

    async deleteDmShop(args) {
      const shopDriver = pickDriverForPath('shopRuntime');
      if (!shopDriver) return null;
      if (typeof shopDriver.deleteDmShop !== 'function') return null;
      return await shopDriver.deleteDmShop(args);
    },

    async updateDmShop(args) {
      const shopDriver = pickDriverForPath('shopRuntime');
      if (!shopDriver) return null;
      if (typeof shopDriver.updateDmShop !== 'function') return null;
      return await shopDriver.updateDmShop(args);
    },

    async createDmShopListing(args) {
      const shopDriver = pickDriverForPath('shopRuntime');
      if (!shopDriver) return null;
      if (typeof shopDriver.createDmShopListing !== 'function') return null;
      return await shopDriver.createDmShopListing(args);
    },

    async deleteDmShopListing(args) {
      const shopDriver = pickDriverForPath('shopRuntime');
      if (!shopDriver) return null;
      if (typeof shopDriver.deleteDmShopListing !== 'function') return null;
      return await shopDriver.deleteDmShopListing(args);
    },

    async updateDmShopListing(args) {
      const shopDriver = pickDriverForPath('shopRuntime');
      if (!shopDriver) return null;
      if (typeof shopDriver.updateDmShopListing !== 'function') return null;
      return await shopDriver.updateDmShopListing(args);
    },

    async updateDmShopRuntimeState(args) {
      const shopDriver = pickDriverForPath('shopRuntime');
      if (!shopDriver) return null;
      if (typeof shopDriver.updateDmShopRuntimeState !== 'function') return null;
      return await shopDriver.updateDmShopRuntimeState(args);
    },

    async publishLootEncounterForDm(args) {
      const lootDriver = pickDriverForPath('lootRuntime');
      if (!lootDriver) return null;
      if (typeof lootDriver.publishLootEncounterForDm !== 'function') return null;
      return await lootDriver.publishLootEncounterForDm(args);
    },

    async importLootDataForDm(args) {
      const lootDriver = pickDriverForPath('lootRuntime');
      if (!lootDriver) return null;
      if (typeof lootDriver.importLootDataForDm !== 'function') return null;
      return await lootDriver.importLootDataForDm(args);
    },

    async claimLootDrop(args) {
      const charDriver = pickDriverForPath('chars');
      const lootDriver = pickDriverForPath('lootRuntime');
      if (!charDriver || !lootDriver || charDriver !== lootDriver) return null;
      if (typeof lootDriver.claimLootDrop !== 'function') return null;
      return await lootDriver.claimLootDrop(args);
    },

    async withdrawLootClaim(args) {
      const charDriver = pickDriverForPath('chars');
      const lootDriver = pickDriverForPath('lootRuntime');
      if (!charDriver || !lootDriver || charDriver !== lootDriver) return null;
      if (typeof lootDriver.withdrawLootClaim !== 'function') return null;
      return await lootDriver.withdrawLootClaim(args);
    },

    async setLootDropVisibilityForDm(args) {
      const lootDriver = pickDriverForPath('lootRuntime');
      if (!lootDriver) return null;
      if (typeof lootDriver.setLootDropVisibilityForDm !== 'function') return null;
      return await lootDriver.setLootDropVisibilityForDm(args);
    },

    async revealAllLootDropsForDm(args) {
      const lootDriver = pickDriverForPath('lootRuntime');
      if (!lootDriver) return null;
      if (typeof lootDriver.revealAllLootDropsForDm !== 'function') return null;
      return await lootDriver.revealAllLootDropsForDm(args);
    },

    async finishLootEncounterForDm(args) {
      const lootDriver = pickDriverForPath('lootRuntime');
      if (!lootDriver) return null;
      if (typeof lootDriver.finishLootEncounterForDm !== 'function') return null;
      return await lootDriver.finishLootEncounterForDm(args);
    },

    async distributeLootXpForDm(args) {
      const charDriver = pickDriverForPath('chars');
      const lootDriver = pickDriverForPath('lootRuntime');
      if (!charDriver || !lootDriver || charDriver !== lootDriver) return null;
      if (typeof lootDriver.distributeLootXpForDm !== 'function') return null;
      return await lootDriver.distributeLootXpForDm(args);
    },

    async awardLootDrop(args) {
      const charDriver = pickDriverForPath('chars');
      const lootDriver = pickDriverForPath('lootRuntime');
      if (!charDriver || !lootDriver || charDriver !== lootDriver) return null;
      if (typeof lootDriver.awardLootDrop !== 'function') return null;
      return await lootDriver.awardLootDrop(args);
    },

    async awardCurrencyLoot(args) {
      const charDriver = pickDriverForPath('chars');
      const lootDriver = pickDriverForPath('lootRuntime');
      if (!charDriver || !lootDriver || charDriver !== lootDriver) return null;
      if (typeof lootDriver.awardCurrencyLoot !== 'function') return null;
      return await lootDriver.awardCurrencyLoot(args);
    },

    flushPending() {
      // Flush all drivers' pending writes. Cheap — most drivers no-op this.
      Object.values(drivers).forEach(d => { try { d.flushPending(); } catch (e) { console.error(e); } });
    },

    dispose() {
      if (disposed) return;
      disposed = true;
      Object.values(drivers).forEach(d => { try { d.dispose(); } catch (e) { console.error(e); } });
    },

    getConnectionStatus() {
      // The routed driver's connection status is the "worst" status across any
      // driver that handles a routed path. With Firebase removed in Session 10,
      // this effectively reports Convex's status (plus local driver if in use).
      const severityOrder = ['error', 'disconnected', 'checking', 'local', 'connected'];
      let worst = 'connected';
      const seen = new Set();
      for (const path of COLLECTION_PATHS) {
        const kind = routing[path] || compositeDefault;
        if (seen.has(kind)) continue;
        seen.add(kind);
        const d = drivers[kind];
        if (!d) continue;
        const s = d.getConnectionStatus();
        if (severityOrder.indexOf(s) < severityOrder.indexOf(worst)) worst = s;
      }
      return worst;
    },
  };
};
